import {EventBus} from '@mintdata-wsp/common/bus/EventBus';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ActiveUserInfo} from '@mintdata-wsp/common/controllers/network/generated/responses/ActiveUserInfo';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {IActiveUserDetails, IBoxMoveState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {ActiveUserEvent, ActiveUsersEvent} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import _throttle from 'lodash/throttle';
import {action, runInAction} from 'mobx';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {TopBarUserIconList} from '@mintdata-wsp/wsp/ui/views/visualDesigner/header/userPresence/TopBarUserIconList';

export class ActiveUserColors
{
    private static readonly colors:string[] = [
        'rgb(60, 179, 113)',   // MEDIUMSEAGREEN
        'rgb(255, 140, 0)',    // DARKORANGE
        'rgb(95, 158, 160)',   // CADETBLUE
        'rgb(199, 21, 133)',   // MEDIUMVIOLETRED
        'rgb(220, 20, 60)',    // CRIMSON
        'rgb(148, 0, 211)',    // DARKVIOLET
        'rgb(139, 69, 19)',    // SADDLEBROWN
        'rgb(240, 128, 128)',  // LIGHTCORAL
        'rgb(189, 183, 107)',  // DARKKHAKI
        'rgb(154, 205, 50)',   // YELLOWGREEN
    ];

    public static getColor(index:number):string
    {
        if( ActiveUserColors.colors.length < index )
        {
            return ActiveUserColors.colors[ActiveUserColors.colors.length % index];
        }
        else
        {
            return ActiveUserColors.colors[index];
        }
    }
}

export class ActiveUsersSyncController
{
    private _connected = false;
    private _pollActiveUsersTimeoutId = null;

    public static readonly IDLE_USER_TIMEOUT_MS = 300000;

    private readonly _throttledSendCurrentUserData = _throttle(this._sendCurrentUserData, 50);

    private _eventsQueue:ActiveUsersEvent[] = [];

    private _boxMoveStateCleanupTimers:Map<string, any> = new Map();

    public init()
    {
        const {user, ui} = controllers;
        const {multiplayer, openDesign, surfaceArea, surfaceSelection, spreadsheets, workbenchVisibility} = ui;
        const {tabs, mesh, meshSelection} = spreadsheets;
        const {designState} = commonControllers;

        EventBus.listen(multiplayer, {
            onConnected: this._onMultiplayerConnected,
            onDisconnected: this._onMultiplayerDisconnected,
            onConnectionLost: this._onConnectionLost,
            onConnectionRestored: this._onConnectionRestored
        });

        // to sync user id
        EventBus.listen(user, {
            onLogin: () => this._throttledSendCurrentUserData(),
        });

        EventBus.listen(openDesign, {
            onInitialized: () =>
            {
                this._throttledSendCurrentUserData();
                this._syncUserDetails();
            },
        });

        EventBus.listen(surfaceArea, {
            onMouseMove: () => this._throttledSendCurrentUserData(),
            onBoxDragInProgress: () =>  this._throttledSendCurrentUserData(),
            onBoxDragStop: () =>  this._throttledSendCurrentUserData(),
            onBoxResizeInProgress: () =>  this._throttledSendCurrentUserData(),
            onBoxResizeStop: () =>  this._throttledSendCurrentUserData(),
            onPanPositionChanged: () =>
            {
                this.stopFollowing();
                this._throttledSendCurrentUserData();
            }
        });

        EventBus.listen(designState, {
            onActivePageChanged: () =>
            {
                this.stopFollowing();
                this._throttledSendCurrentUserData();
            }
        });

        EventBus.listen(surfaceSelection, {
            onSurfaceSelectionChanged: () => this._throttledSendCurrentUserData(),
        });

        EventBus.listen(tabs, {
            onActiveSheetChanged: () =>
            {
                this.stopFollowing();
                this._throttledSendCurrentUserData();
            }
        });

        EventBus.listen(mesh, {
            onMouseMove: () => this._throttledSendCurrentUserData(),
            onSheetScrolled: () =>
            {
                this.stopFollowing();
                this._throttledSendCurrentUserData();
            }
        });

        EventBus.listen(meshSelection, {
            onSheetSelectionChanged: () => this._throttledSendCurrentUserData(),
        });

        EventBus.listen(workbenchVisibility, {
            onSpreadsheetPanelWidthChange: () =>
            {
                this.stopFollowing();
                this._throttledSendCurrentUserData();
            }
        });

    }

    @bind
    private _onMultiplayerConnected()
    {
        const {multiplayer} = controllers.ui;

        this._connected = true;

        multiplayer.subscribeToEvents('sync-users', this._onGetActiveUsers);

        if( rootState.userSession.currentUser && rootState.userSession.currentUser.id > 0 )
            this._sendCurrentUserData();
    }

    @bind
    private _onMultiplayerDisconnected()
    {
        this._onConnectionLost();
    }

    @action.bound
    private _onConnectionLost()
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        activeUsers.followingUserId = 0;
        activeUsers.otherUsers.clear();
        activeUsers.otherUsersDetails.clear();

        this._connected = false;
    }

    @bind
    private _onConnectionRestored()
    {
        this._onMultiplayerConnected();
    }

    private _sendCurrentUserData()
    {
        const {net} = rootState;

        if( !this._connected || net.isDesignSaveTimedOut || net.isNetworkTooSlowForMP )
            return;

        const {multiplayer, spreadsheets, spreadsheets: {mesh}, surfaceSelection} = controllers.ui;
        const {currentUser} = rootState.userSession;
        const {visualDesigner} = rootState.ui;
        const {thisUser} = rootState.ui.visualDesigner.activeUsers;
        const {designState} = commonControllers;

        const activePage = designState.getActivePage();
        const activeSheet = spreadsheets.main.getActiveSheet();
        const selectedComponents = surfaceSelection.getSelectedBoxes().map(box => box.componentDesc.box.id);

        const ctx:IWSPMeshContext | null = mesh.getContextOfFocusedSheet();
        const meshState = ctx ? mesh.getState(ctx) : null;

        const spreadsheetState = () =>
        {
            if( !visualDesigner.workbench.visibility.isSpreadsheetVisible )
                return 0;

            if( visualDesigner.workbench.visibility.isSpreadsheetFullScreen )
                return 100;

            return visualDesigner.workbench.visibility.layout.gridRightColumnWidth;
        }

        const event:ActiveUserEvent = {
            userId: currentUser.id,
            timestamp: new Date().getTime(),
            cursorAt: thisUser.cursorAt,
            mouseX: thisUser.mouseX,
            mouseY: thisUser.mouseY,
            page: activePage ? activePage.id : null,
            components: selectedComponents,
            sheet: activeSheet ? activeSheet.id : null,
            range: meshState ? meshState.details.selectedRange.asKey() : null,

            surfacePanX: visualDesigner.surfaceEditor.area.panModeState.position.x,
            surfacePanY: visualDesigner.surfaceEditor.area.panModeState.position.y,
            surfaceZoom: visualDesigner.surfaceEditor.area.zoom,

            spreadsheetPanelWidth: spreadsheetState(),
            sheetScrollX: meshState ? meshState.alignedScrollLeft : null,
            sheetScrollY: meshState ? meshState.alignedScrollTop : null,

            boxMoveState: thisUser.boxMoveState,
        };

        multiplayer.sendEvent('sync-users', event);
    }

    @bind
    private async _onGetActiveUsers(event?:ActiveUsersEvent)
    {
        this._eventsQueue.push(event);

        setTimeout(() => this._applyActiveUsersIfNeeded());
    }

    @action.bound
    private async _applyActiveUsersIfNeeded()
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( !this._eventsQueue.length )
            return;

        const lastEventUsers = this._eventsQueue.pop().users;

        for( const lastEventUser of lastEventUsers)
        {
            const userIndex = activeUsers.otherUsers.findIndex(user => user.userId === lastEventUser.userId);

            if( userIndex === -1 )
            {
                activeUsers.otherUsers.push(lastEventUser)
            }
            else {
                if( activeUsers.otherUsers[userIndex].timestamp > lastEventUser.timestamp )
                    return;

                activeUsers.otherUsers[userIndex] = lastEventUser;

                const userId = activeUsers.otherUsers[userIndex].userId;

                if( lastEventUser.components )
                {
                    for( const boxId of lastEventUser.components )
                    {
                        const selectionState = activeUsers.otherUserSelectionState.get(boxId) || [];

                        if( selectionState.findIndex(moveState => moveState.userId === userId) === -1 )
                            selectionState.push({userId});

                        activeUsers.otherUserSelectionState.set(boxId, selectionState);
                    }
                }

                // clear selections
                activeUsers.otherUserSelectionState.forEach((users, boxId) =>
                {
                    if( users.length && users.some(user => user.userId === userId) && !lastEventUser.components.includes(boxId) )
                    {
                        if( users.length === 1)
                        {
                            activeUsers.otherUserSelectionState.delete(boxId);
                        }
                        else
                        {
                            const index = users.findIndex(user => user.userId === userId);
                            users.splice(index, 1);
                        }
                    }
                });

                if( lastEventUser.boxMoveState )
                {
                    for( const boxId of Object.keys(lastEventUser.boxMoveState) )
                    {
                        const newState:IBoxMoveState = {
                            userId,
                            ...lastEventUser.boxMoveState[boxId]
                        }

                        const boxMoveState = activeUsers.otherUserBoxMoveState.get(boxId) || [];

                        let index = boxMoveState.findIndex(moveState => moveState.userId === userId)

                        if( index !== -1 )
                            boxMoveState[index] = newState;
                        else
                            boxMoveState.push(newState);

                        activeUsers.otherUserBoxMoveState.set(boxId, boxMoveState);

                        if( newState.isReparenting )
                        {
                            const boxReparentingState = activeUsers.otherUserBoxReparentingState.get(boxId) || [];

                            index = boxReparentingState.findIndex(moveState => moveState.userId === userId)

                            if( index !== -1 )
                                boxReparentingState[index] = newState;
                            else
                                boxReparentingState.push(newState);

                            activeUsers.otherUserBoxReparentingState.set(boxId, boxReparentingState);
                        }
                    }
                }
                else
                {
                    activeUsers.otherUserBoxMoveState.forEach((boxMoveState, boxId) =>
                    {
                        if( boxMoveState && boxMoveState.some(moveState => moveState.userId === userId) )
                        {
                            this._markBoxMoveStateAsDone(boxId, userId);
                            if( !this._boxMoveStateCleanupTimers.has(boxId) )
                            {
                                this._boxMoveStateCleanupTimers.set(boxId, setTimeout(() => this.clearBoxMoveState(boxId), 1000) );
                            }
                        }
                    });

                    activeUsers.otherUserBoxReparentingState.forEach((boxReparentingState, boxId) =>
                    {
                        if( boxReparentingState && boxReparentingState.some(reparentingState => reparentingState.userId === userId) )
                        {
                            this._markBoxMoveStateAsDone(boxId, userId);
                            if( !this._boxMoveStateCleanupTimers.has(boxId) )
                            {
                                this._boxMoveStateCleanupTimers.set(boxId, setTimeout(() => this.clearBoxMoveState(boxId), 1000) );
                            }
                        }
                    })
                }
            }
        }

        for( let i = 0; i < activeUsers.otherUsers.length; i++)
        {
            if( !lastEventUsers.some(user => user.userId === activeUsers.otherUsers[i].userId) )
            {
                activeUsers.otherUsers.splice(i--, 1);
            }
        }

        await this._syncUserDetails();
        this.updateFollowingState();

        this._eventsQueue = [];
    }


    @action.bound
    private async _syncUserDetails(usersEvent?:ActiveUserEvent[])
    {
        const {activeUsers, openDesign} = rootState.ui.visualDesigner;
        const {currentUser} = rootState.userSession;
        
        const users = usersEvent || activeUsers.otherUsers;

        for(let userIndex = 0; userIndex < users.length; userIndex++)
        {
            if( !openDesign || !openDesign.designRef )
                return;

            const user = users[userIndex];

            if( !activeUsers.otherUsersDetails.has(user.userId) )
            {
                await API.organizations.activeUsers(rootState.userSession.currentOrganization.siteId).then(response =>
                {
                    response.activeUsers
                        .filter(user => user.id !== currentUser.id && user.designId === openDesign.designRef.designId)
                        .forEach((userDetails, userIndex) =>
                        {
                            this._addUserDetails(userDetails, userIndex);
                        });
                });
            }
            else
            {
                this._updateUserDetails(user.userId, userIndex);
            }
        }
    }

    @action
    private _addUserDetails(userDetails:ActiveUserInfo, userIndex:number)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( activeUsers.otherUsersDetails.has(userDetails.id) )
            clearTimeout(activeUsers.otherUsersDetails.get(userDetails.id).idleHandlerId);

        const user = activeUsers.otherUsers
            .filter(userEvent => userEvent.userId == userDetails.id)[0];

        if( !user )
            return;

        const lastEventTimestamp = user.timestamp;

        const timeToIdle = lastEventTimestamp + ActiveUsersSyncController.IDLE_USER_TIMEOUT_MS - new Date().getTime();

        activeUsers.otherUsersDetails.set(userDetails.id, {
            fullName: userDetails.fullName,
            avatarUrl: userDetails.avatarUrl,
            color: ActiveUserColors.getColor(userIndex),
            isIdle: lastEventTimestamp < new Date().getTime() - ActiveUsersSyncController.IDLE_USER_TIMEOUT_MS,
            idleHandlerId: setTimeout(() => this._idleUserHandler(userDetails.id), timeToIdle),
            lastUpdated: user.timestamp
        });
    }

    @action
    private _updateUserDetails(userId:number, userIndex:number)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( !activeUsers.otherUsersDetails.has(userId) )
            return;

        let userDetails = activeUsers.otherUsersDetails.get(userId);

        userDetails.color = ActiveUserColors.getColor(userIndex);

        const user = activeUsers.otherUsers
            .filter(userEvent => userEvent.userId == userId)[0];

        const lastEventTimestamp = user.timestamp;
            
        if( userDetails.lastUpdated !== lastEventTimestamp )
        {
            clearTimeout(userDetails.idleHandlerId);
            const timeToIdle = lastEventTimestamp + ActiveUsersSyncController.IDLE_USER_TIMEOUT_MS - new Date().getTime();
            userDetails.isIdle = lastEventTimestamp < new Date().getTime() - ActiveUsersSyncController.IDLE_USER_TIMEOUT_MS,
            userDetails.idleHandlerId = setTimeout(() => this._idleUserHandler(userId), timeToIdle)
        }

        activeUsers.otherUsersDetails.set(userId, userDetails); 
    }

    @action
    private _idleUserHandler(userId:number)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( activeUsers.otherUsersDetails.has(userId) )
        {
            const userDetails = activeUsers.otherUsersDetails.get(userId);

            clearTimeout(userDetails.idleHandlerId);
            userDetails.isIdle = true;

            activeUsers.otherUsersDetails.set(userId, userDetails);
        }
    }

    @action.bound
    public updateFollowingState()
    {
        const {designState} = commonControllers;
        const {activeUsers, workbench} = rootState.ui.visualDesigner;
        const {spreadsheets: {mesh, main, tabs}} = controllers.ui;

        if( activeUsers.followingUserId )
        {
            const userToFollow = activeUsers.otherUsers.filter(user => user.userId === activeUsers.followingUserId)[0];

            if( userToFollow )
            {
                this.updateFollowingSlot();

                if( userToFollow.page )
                    designState.getDesignState().surface.uiState.activePageId = userToFollow.page;

                if( userToFollow.sheet )
                    tabs.setActiveSheet(main.getRootWorkbook().id, userToFollow.sheet, false);

                controllers.ui.surfaceArea.setPanPosition({x: userToFollow.surfacePanX || 0, y: userToFollow.surfacePanY || 0}, false);
                controllers.ui.surfaceArea.setZoom(userToFollow.surfaceZoom || 1);

                if ( typeof userToFollow.spreadsheetPanelWidth !== 'undefined' )
                {
                    if( userToFollow.spreadsheetPanelWidth === 0 )
                    {
                        workbench.visibility.isSpreadsheetVisible = false;
                        workbench.visibility.isSpreadsheetFullScreen = false;
                    }
                    else if( userToFollow.spreadsheetPanelWidth === 100 )
                    {
                        workbench.visibility.isSpreadsheetVisible = true;
                        workbench.visibility.isSpreadsheetFullScreen = true;
                    }
                    else
                    {
                        workbench.visibility.isSpreadsheetVisible = true;
                        workbench.visibility.isSpreadsheetFullScreen = false;
                        workbench.visibility.layout.gridRightColumnWidth = userToFollow.spreadsheetPanelWidth;
                    }
                }

                const ctx:IWSPMeshContext | null = mesh.getContextOfFocusedSheet();

                if( ctx )
                    mesh.setMeshScrollPosition(ctx, userToFollow.sheetScrollX || 0, userToFollow.sheetScrollY || 0, false);
            }
            else
            {
                // disconnected?
                activeUsers.followingUserId = 0;
            }
        }
    }

    @action
    public updateFollowingSlot()
    {
        const {activeUsers} = rootState.ui.visualDesigner;
        const users = activeUsers.otherUsers;

        if( activeUsers.followingUserId )
        {
            const followingUserPos = users.findIndex(user => user.userId === activeUsers.followingUserId);

            if( followingUserPos > TopBarUserIconList.MAX_TOPBAR_VISIBLE_USERS-1 )
            {
                const followingUser = users[followingUserPos];
                users[followingUserPos] = users[TopBarUserIconList.MAX_TOPBAR_VISIBLE_USERS-1];
                users[TopBarUserIconList.MAX_TOPBAR_VISIBLE_USERS-1] = followingUser;

                activeUsers.otherUsers = users;
            }
        }
    }

    @action
    public stopFollowing()
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( activeUsers.followingUserId !== 0 )
            activeUsers.followingUserId = 0;
    }

    @action
    private _markBoxMoveStateAsDone(boxId:string, userId:number)
    {
        const {activeUsers} = rootState.ui.visualDesigner;
        const moveState:IBoxMoveState[] = activeUsers.otherUserBoxMoveState.get(boxId);

        if( moveState && moveState.length )
        {
            const userIndex = moveState.findIndex(moveState => moveState.userId === userId);

            if( userIndex !== -1 )
            {
                moveState[userIndex].isDone = true;
                activeUsers.otherUserBoxMoveState.set(boxId, moveState);
            }
        }

        const reparentingState:IBoxMoveState[] = activeUsers.otherUserBoxReparentingState.get(boxId);

        if( reparentingState && reparentingState.length )
        {
            const userIndex = reparentingState.findIndex(reparentingState => reparentingState.userId === userId);

            if( userIndex !== -1 )
            {
                reparentingState[userIndex].isDone = true;
                activeUsers.otherUserBoxReparentingState.set(boxId, reparentingState);
            }
        }
    }

    @action
    public clearBoxMoveState(boxId?:string)
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        if( boxId )
        {
            let moveState = activeUsers.otherUserBoxMoveState.get(boxId);
            
            if( moveState && moveState.length )
            {
                const nextMoveState = moveState.filter(moveState => !moveState.isDone);

                if( nextMoveState.length < moveState.length )
                {
                    if( nextMoveState.length )
                        activeUsers.otherUserBoxMoveState.set(boxId, nextMoveState);
                    else
                        activeUsers.otherUserBoxMoveState.delete(boxId);
                }
            }

            let reparentingState = activeUsers.otherUserBoxReparentingState.get(boxId);
            
            if( reparentingState && reparentingState.length )
            {
                const nextReparentingState = reparentingState.filter(reparentingState => !reparentingState.isDone);

                if( nextReparentingState.length < reparentingState.length )
                {
                    if( nextReparentingState.length )
                        activeUsers.otherUserBoxReparentingState.set(boxId, nextReparentingState);
                    else
                        activeUsers.otherUserBoxReparentingState.delete(boxId);
                }
            }

            this._boxMoveStateCleanupTimers.delete(boxId);
        }
        else
        {
            activeUsers.otherUserBoxMoveState.clear();
            activeUsers.otherUserBoxReparentingState.clear();
            this._boxMoveStateCleanupTimers.clear();
        }
    }

    @bind
    private _pollActiveUsers()
    {
        API.organizations.activeUsers(rootState.userSession.currentOrganization.siteId).then((response) =>
        {
            if( !response.activeUsers )
                return;

            runInAction(() =>
            {
                const users:ActiveUserEvent[] = [];

                response.activeUsers.forEach((user, index) =>
                {
                    users.push({userId: user.id} as ActiveUserEvent);
                    rootState.ui.visualDesigner.activeUsers.otherUsersDetails.set(user.id, {
                        fullName: user.fullName,
                        avatarUrl: user.avatarUrl,
                        color: ActiveUserColors.getColor(index)
                    } as IActiveUserDetails);
                });

                rootState.ui.visualDesigner.activeUsers.otherUsers.replace(users);
            });
        });
    }

    @bind
    public startSyncWSPUsers()
    {
        if( !this._pollActiveUsersTimeoutId )
        {
            this._pollActiveUsers();
            this._pollActiveUsersTimeoutId = setInterval(this._pollActiveUsers, 10000);
        }
    }

    @bind
    public stopSyncWSPUsers()
    {
        if( this._pollActiveUsersTimeoutId )
        {
            window.clearInterval(this._pollActiveUsersTimeoutId);
            this._pollActiveUsersTimeoutId = null;
        }
    }
}
